import logger from '~/services/logger';
import { twMerge } from 'tailwind-merge';
import { responsiveBreakpoints } from '~/services/layout/layout';
import {
  getCellVisibillityClasses,
  type ResponsiveSources,
} from './power-cell-getters';

type BaseParams = {
  imageSources: ResponsiveSources;
  hoverSources: ResponsiveSources;
  imageClassNames: string;
  imageClassNamesOnHover: string;
  imageParentClassNames: string;
  hoverClassNames: string;
  hoverParentClassNames: string;
  altText: string;
};

type ExclusiveParams =
  | {
      powerCellImage?: { isPowerCellImageHover?: boolean };
      isHoverImage?: never;
    }
  | { powerCellImage?: never; isHoverImage?: boolean };

type ResponsiveImageParams = BaseParams & ExclusiveParams;

/**
 * Type guard that checks whether a value is a non-empty string.
 * @param src - Value to check
 * @returns True if src is a non-empty string
 */
const isValidSource = (src: unknown): src is string =>
  typeof src === 'string' && src.trim() !== '';

const TRANSPARENT_IMAGE_SRC =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR4nGNgYAAAAAMAASsJTYQAAAAASUVORK5CYII=';

/**
 * Renders the image content for each breakpoint
 * @param imageSources - Image sources for each breakpoint
 * @param imageClassNames - Class names for the image
 * @param imageClassNamesOnHover - Class names for the image on hover
 * @param imageParentClassNames - Class names for the parent of image
 * @param hoverParentClassNames - Class names for the parent of hover
 * @param hoverSources - Hover sources for each breakpoint
 * @param hoverClassNames - Class names for the hover
 * @param altText - Alt text for the image
 * @returns - JSX.Element with responsive image
 */
export function ResponsiveImageWithHover({
  imageSources,
  imageClassNames,
  imageClassNamesOnHover,
  hoverSources,
  hoverClassNames,
  altText,
  imageParentClassNames,
  hoverParentClassNames,
}: ResponsiveImageParams): JSX.Element {
  try {
    return (
      <>
        <div
          className={imageParentClassNames}
          data-testid="ContentBadgeDefault"
        >
          <picture>
            {isValidSource(imageSources.mobile) && (
              <source
                srcSet={imageSources.mobile}
                media={`(max-width: ${responsiveBreakpoints.tablet - 1}px)`}
              />
            )}
            {isValidSource(imageSources.tablet) && (
              <source
                srcSet={imageSources.tablet}
                media={`(max-width: ${
                  responsiveBreakpoints.desktop - 1
                }px) and (min-width: ${responsiveBreakpoints.tablet}px)`}
              />
            )}
            {isValidSource(imageSources.desktop) && (
              <source
                srcSet={imageSources.desktop}
                media={`(max-width: ${
                  responsiveBreakpoints['large-desktop'] - 1
                }px) and (min-width: ${responsiveBreakpoints.desktop}px)`}
              />
            )}
            {isValidSource(imageSources['large-desktop']) && (
              <source
                srcSet={imageSources['large-desktop']}
                media={`(min-width: ${responsiveBreakpoints['large-desktop']}px)`}
              />
            )}

            <img
              src={TRANSPARENT_IMAGE_SRC}
              alt={altText}
              className={twMerge(
                getCellVisibillityClasses(imageSources, imageClassNames),
                imageClassNamesOnHover
              )}
            />
          </picture>
        </div>

        <div className={hoverParentClassNames} data-testid="ContentBadgeHover">
          <picture>
            {isValidSource(hoverSources.mobile) && (
              <source
                srcSet={hoverSources.mobile}
                media={`(max-width: ${responsiveBreakpoints.tablet - 1}px)`}
              />
            )}
            {isValidSource(hoverSources.tablet) && (
              <source
                srcSet={hoverSources.tablet}
                media={`(max-width: ${
                  responsiveBreakpoints.desktop - 1
                }px) and (min-width: ${responsiveBreakpoints.tablet}px)`}
              />
            )}
            {isValidSource(hoverSources.desktop) && (
              <source
                srcSet={hoverSources.desktop}
                media={`(max-width: ${
                  responsiveBreakpoints['large-desktop'] - 1
                }px) and (min-width: ${responsiveBreakpoints.desktop}px)`}
              />
            )}
            {isValidSource(hoverSources['large-desktop']) && (
              <source
                srcSet={hoverSources['large-desktop']}
                media={`(min-width: ${responsiveBreakpoints['large-desktop']}px)`}
              />
            )}

            <img
              src={TRANSPARENT_IMAGE_SRC}
              alt={altText}
              className={twMerge(
                getCellVisibillityClasses(hoverSources, hoverClassNames),
                'hidden group-hover:block'
              )}
            />
          </picture>
        </div>
      </>
    );
  } catch (error: any) {
    logger.info(`ResponsiveImage: ${error.message}`);
    return <></>;
  }
}
